import type { AxiosError } from 'axios';
import { AuthManager } from '../config/auth.config';
import {
  AUTO_REPORT_EMAIL_DOMAIN,
  SKIP_ANALYTICS_HEADER,
  SUBMIT_TICKET,
} from '../constants/reportBugConstants';
import type { AsanaTaskPayload } from '../api/reportBug.api';
import { logger } from './logger';

/**
 * Faithful port of `RW-ERP-Service/src/utils/bugLogger.ts` (`sendToAnalytics`) —
 * silently files an Asana bug ticket in the fixed "Error Log" project
 * (`SUBMIT_TICKET.ERROR_LOG_PROJECT_ID`) whenever an internal
 * (`AUTO_REPORT_EMAIL_DOMAIN`) user's API call fails with `status >= 400`
 * (excluding `401`), unless the failing request was itself marked
 * `SKIP_ANALYTICS_HEADER` (the Asana "create task" call sets this to avoid
 * recursively reporting its own failures). Deliberately does NOT send
 * `customFields` — the fixed "Error Log" project isn't configured with the
 * priority/type/team/domain fields the manual Submit Ticket flow's QA/Prod
 * projects have, and sending those field GIDs makes Asana reject the task.
 *
 * Hooked from `axios.config.ts`'s response-interceptor error branch,
 * fire-and-forget (`void reportApiFailureToAnalytics(error)`) — a slow or
 * failing Asana call must never delay or alter the original rejection.
 */

/**
 * Status-code trigger range this function's own contract promises (see the file
 * doc comment: "status >= 400, excluding 401"). Enforced HERE, inside the
 * function itself, rather than trusted to whatever guard a caller happens to
 * have — `axios.config.ts`'s interceptor already filters to this same range
 * before calling in, but an exported function that documents a contract has to
 * hold that contract on its own, or a future/different call site silently
 * breaks it (e.g. flooding the shared Error Log project with routine 401s).
 */
const HTTP_STATUS_CLIENT_ERROR_THRESHOLD = 400;
const HTTP_STATUS_UNAUTHORIZED = 401;

// Fallback sentinel labels used throughout the built description when a field is
// absent — named so they stay consistent if this fallback semantics is ever reused
// (e.g. by the manual Submit Ticket flow).
const UNKNOWN_URL_LABEL = 'Unknown URL';
const UNKNOWN_METHOD_LABEL = 'UNKNOWN';
const NO_PAYLOAD_LABEL = 'No payload';
const NO_RESPONSE_DATA_LABEL = 'No response data';
const NO_MESSAGE_LABEL = 'No message provided';

const REDACTED_VALUE = '[REDACTED]';

/**
 * Key-name fragments (case-insensitive, matched anywhere in the key) that mark a
 * request/response body field as sensitive — e.g. `password`, `newPassword`,
 * `refreshToken`, `x-api-key` all match. Deliberately broad: a false-positive
 * redaction just loses a harmless field from the ticket description, while a
 * false negative leaks a secret into Asana.
 */
const SENSITIVE_BODY_KEY_PATTERNS = [
  'password',
  'token',
  'secret',
  'authorization',
  'apikey',
  'cookie',
  'ssn',
  'creditcard',
  'cardnumber',
  'cvv',
  'pin',
  'otp',
] as const;

/** Caps recursion so a deeply-nested (or adversarial) body can't blow the stack. */
const MAX_REDACTION_DEPTH = 6;

/** Header value shape axios may hand back for a single header. */
type HeaderValue = string | number | boolean | string[] | null | undefined;

interface AxiosHeadersLike {
  get?: (header: string) => HeaderValue;
  toJSON?: () => Record<string, unknown>;
}

/**
 * True when the failing request explicitly opted out of automatic
 * reporting (`SKIP_ANALYTICS_HEADER`). Handles both a plain header object
 * and an `AxiosHeaders` instance — the latter is what axios hands back on
 * a real request; a plain object shows up in unit tests and in any config
 * built by hand.
 */
function hasSkipAnalyticsHeader(headers: unknown): boolean {
  if (!headers) return false;
  const headersLike = headers as AxiosHeadersLike;
  if (typeof headersLike.get === 'function') {
    return Boolean(headersLike.get(SKIP_ANALYTICS_HEADER));
  }
  return Boolean((headers as Record<string, unknown>)[SKIP_ANALYTICS_HEADER]);
}

/**
 * Strips separators before matching so hyphen/underscore/space-separated key
 * forms (`X-Api-Key`, `api_key`, `Api Key`) still match the same pattern as
 * the unseparated body-field form (`apiKey`).
 */
function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
  return SENSITIVE_BODY_KEY_PATTERNS.some((pattern) => normalized.includes(pattern));
}

/**
 * Copies the failing request's headers into a plain object with every
 * sensitive header (per `SENSITIVE_BODY_KEY_PATTERNS` — `Authorization`,
 * any casing of it, `X-Api-Key`, `Cookie`, etc.) redacted, for safe inclusion
 * in the ticket description. `AxiosHeaders.toJSON()` is used when available
 * so an `AxiosHeaders` instance never silently spreads into `{}`.
 */
function redactHeaders(headers: unknown): Record<string, unknown> {
  if (!headers) return {};
  const headersLike = headers as AxiosHeadersLike;
  const plain: Record<string, unknown> =
    typeof headersLike.toJSON === 'function'
      ? headersLike.toJSON()
      : { ...(headers as Record<string, unknown>) };
  const redacted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(plain)) {
    redacted[key] = isSensitiveKey(key) ? REDACTED_VALUE : value;
  }
  return redacted;
}

/**
 * Extracts the path segment of a URL after the API version (e.g. `/v1/`,
 * `/v2/`), stripping any query string and leading slashes. Falls back to
 * the raw URL when no version segment is found. Pure function — mirrors
 * `RW-ERP-Service/src/utils/bugLogger.ts#extractUrlPath`.
 */
function extractUrlPath(url: string): string {
  const versionMatch = /\/v\d+\//.exec(url);
  if (!versionMatch) return url;

  let path = url.substring(url.indexOf(versionMatch[0]) + versionMatch[0].length);

  const queryIndex = path.indexOf('?');
  if (queryIndex !== -1) {
    path = path.substring(0, queryIndex);
  }

  return path.replace(/^\/+/, '');
}

/**
 * Recursively redacts sensitive fields (password/token/secret/etc., per
 * `SENSITIVE_BODY_KEY_PATTERNS`) out of a request/response body before it is
 * embedded in the Asana ticket description — a failing endpoint's payload can
 * carry a password, token, or other PII, and this ticket is visible to everyone
 * with access to the "Error Log" project. Only plain objects/arrays are
 * inspected; a string body is included as-is (matching the Vue original), since
 * it cannot be safely inspected key-by-key.
 */
function redactSensitiveBody(value: unknown, depth = 0): unknown {
  if (depth >= MAX_REDACTION_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveBody(item, depth + 1));
  }
  const redacted: Record<string, unknown> = {};
  for (const [key, fieldValue] of Object.entries(value as Record<string, unknown>)) {
    redacted[key] = isSensitiveKey(key)
      ? REDACTED_VALUE
      : redactSensitiveBody(fieldValue, depth + 1);
  }
  return redacted;
}

function stringifyBody(value: unknown, emptyLabel: string): string {
  if (value === undefined || value === null || value === '') return emptyLabel;
  return typeof value === 'string' ? value : JSON.stringify(redactSensitiveBody(value), null, 2);
}

/**
 * Narrows an unknown Axios response body down to its `message` field, without
 * an unchecked cast — returns `undefined` when the body isn't a `message`-
 * bearing object (e.g. the error envelope shape drifted) rather than silently
 * assuming a shape that no longer holds.
 */
function extractResponseMessage(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null || !('message' in data)) {
    return undefined;
  }
  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' ? message : undefined;
}

/**
 * Fire-and-forget submission of the built ticket to Asana. Never rethrows —
 * a failed auto-report must never surface to the user or break the
 * original request flow. Uses a LAZY dynamic import of `createBugTask`
 * (not a static top-level import) to avoid a load-time cycle:
 * `axios.config.ts -> bugLogger.ts -> reportBug.api.ts -> axios.config.ts`.
 */
async function submitAutoReport(payload: AsanaTaskPayload): Promise<void> {
  try {
    const { createBugTask } = await import('../api/reportBug.api');
    await createBugTask(payload);
  } catch (error) {
    logger.error('[bugLogger] reportApiFailureToAnalytics failed', error);
  }
}

/**
 * Response-interceptor hook: builds and files an Asana bug ticket for a
 * failing API call, gated to internal staff and skippable via
 * `SKIP_ANALYTICS_HEADER`. Call unawaited (`void reportApiFailureToAnalytics(error)`)
 * from the interceptor — this never throws.
 */
export function reportApiFailureToAnalytics(error: AxiosError): void {
  const status = error.response?.status;
  if (
    status === undefined ||
    status < HTTP_STATUS_CLIENT_ERROR_THRESHOLD ||
    status === HTTP_STATUS_UNAUTHORIZED
  ) {
    return;
  }

  const user = AuthManager.getUser();
  const email = user?.email;
  if (typeof email !== 'string' || !email.endsWith(AUTO_REPORT_EMAIL_DOMAIN)) {
    return;
  }

  if (hasSkipAnalyticsHeader(error.config?.headers)) {
    return;
  }

  const displayName =
    (typeof user?.name === 'string' && user.name) ||
    (typeof user?.displayName === 'string' && user.displayName) ||
    (typeof user?.email === 'string' && user.email) ||
    '';

  const url = error.config?.url ?? UNKNOWN_URL_LABEL;
  const method = error.config?.method?.toUpperCase() ?? UNKNOWN_METHOD_LABEL;
  const message = extractResponseMessage(error.response?.data) ?? error.message ?? NO_MESSAGE_LABEL;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : UNKNOWN_URL_LABEL;

  const headersString = JSON.stringify(redactHeaders(error.config?.headers), null, 2);
  const requestPayload = stringifyBody(error.config?.data, NO_PAYLOAD_LABEL);
  const responseData = stringifyBody(error.response?.data, NO_RESPONSE_DATA_LABEL);
  const formattedPath = extractUrlPath(url);

  // An array joined with '\n' rather than an indented template literal — indentation
  // matching the surrounding source would otherwise bake literal leading spaces into
  // every line of the description, which Asana (and most Markdown renderers) treat
  // as a preformatted block.
  const description = [
    'API Failure Detected',
    `- Current Page: ${currentUrl}`,
    `- API URL: ${url}`,
    `- Method: ${method}`,
    `- Status: ${status}`,
    `- Message: ${message}`,
    `- Time: ${new Date().toString()}`,
    '',
    '- Request Headers:',
    headersString,
    '',
    '- Request Payload:',
    requestPayload,
    '',
    '- Response Data:',
    responseData,
    '',
    `Issue faced by: ${email}`,
  ].join('\n');

  // Deliberately no `customFields` here: unlike the manual Submit Ticket flow (which targets
  // the QA/Prod projects and always sets priority/type/team/domain), the Vue original's
  // automatic reporter never sends custom fields, and the fixed "Error Log" project this
  // hook targets does not have those fields configured — sending their GIDs makes Asana
  // reject the task. Match the Vue original exactly: title, projects, description only.
  const payload: AsanaTaskPayload = {
    title: `${displayName} | ${status} | ${method} : ${formattedPath}`,
    projects: [SUBMIT_TICKET.ERROR_LOG_PROJECT_ID],
    description,
  };

  void submitAutoReport(payload);
}
